import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { TimeInputProps } from './types';
import { InputPattern, UnitText, TimeInputContainer } from './styles';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import Input from '../Input/Input';

const TimeInput: React.FC<TimeInputProps> = (props) => {
  const {
    input,
    meta,
    placeholder,
    label,
    readOnly,
    disabled,
    testId,
    size = 'standard',
    variant = 'underline',
    helperText,
    showClearIcon = false,
    unit,
    value,
    interval,
    onChange,
    onBlur,
    onFocus,
    onMouseDown,
  } = props;
  const { actualTheme } = useContext(ThemeContext);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastValue = useRef('');
  const [internalValue, setInternalValue] = useState('');
  const [onChangeFlag, setonChangeFlag] = useState(false);
  const hasError = useMemo(() => meta && meta.invalid && meta.touched, [meta]);
  const inputValue = useMemo(() => input?.value || internalValue, [input?.value, internalValue]);
  const pattern = useMemo(() => '--:--'.slice(inputValue?.length || 0, 6), [inputValue]);

  const isValidHour = (h: number) => Number.isInteger(h) && h >= 0 && h < 24;
  const isValidMinutes = (m: number) =>
    (Number.isInteger(m) && m >= 0 && m < 60) || Number.isNaN(m);

  const isValidTime = useCallback((val: string) => {
    const [hoursStr, minutesStr] = val.split(':');
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);

    if (!isValidHour(hours) || !isValidMinutes(minutes)) return false;
    if (hours < 10 && Number(hoursStr[0]) > 2) return false;
    if (hoursStr.length > 2) return false;
    if (minutes < 10 && Number(minutesStr[0]) > 5) return false;

    return true;
  }, []);

  const handleTimeValidation = useCallback(
    (val: string) => {
      const regexp = /^\d{0,2}?:?\d{0,2}$/;
      if (!regexp.test(val)) {
        return null;
      }

      let validatedValue = val;
      const len = val.length;
      const lastLen = lastValue.current.length;

      if (isValidTime(val)) {
        if (len === 2 && lastLen !== 3 && val.indexOf(':') === -1) {
          validatedValue = `${val}:`;
        }
        if (len === 2 && lastLen === 3) {
          validatedValue = val.slice(0, 1);
        }
      } else if (len > 0 && Number(val[0]) > 2) {
        if (val.indexOf(':') === -1) validatedValue = `0${val[0]}:${val.slice(1)}`;
        else if (len === 5) validatedValue = `0${val.slice(1)}`;
        else validatedValue = `0${val}`;
      } else if (len === 2 && Number(val[0]) === 2 && Number(val[1]) > 3) {
        validatedValue = '23:';
      } else if (len > 2 && val.indexOf(':') === -1) {
        validatedValue = `${val.slice(0, 2)}:${val.slice(2)}`;
      } else if (len === 4 && Number(val[3]) > 5) {
        validatedValue = `${val.slice(0, 3)}0${val[3]}`;
      }

      lastValue.current = validatedValue;

      return validatedValue;
    },
    [lastValue, isValidTime],
  );

  const validateInterval = (timeValue: string): string => {
    const [hoursStr, minutesStr] = timeValue.split(':');
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);

    if (interval === 60) {
      if (minutes < 30) return timeValue;
      if (hours + 1 === 24) return '00:00';
      const h = hours + 1 >= 10 ? `${hours + 1}` : `0${hours + 1}`;
      return `${h}:${minutesStr}`;
    }

    const div = minutes / interval;
    const min = Math.round(div) * interval;
    if (min === 60) {
      if (hours + 1 === 24) return '00:00';
      const h = hours + 1 >= 10 ? `${hours + 1}` : `0${hours + 1}`;
      return `${h}:00`;
    }

    const minStr = min >= 10 ? `${min}` : `0${min}`;
    return `${hoursStr}:${minStr}`;
  };

  const handleChange = useCallback(
    (val: string) => {
      setonChangeFlag(true);
      const checkedValue = handleTimeValidation(val);
      if (val === '' || checkedValue) {
        setInternalValue(checkedValue);
        if (input) input.onChange(checkedValue);
        if (onChange) onChange(checkedValue);
      }
    },
    [input, handleTimeValidation, onChange],
  );

  const handleClear = useCallback(() => {
    if (input) {
      input.onChange('');
    }
    if (onChange) {
      onChange('');
    }
  }, [input, onChange]);

  const handleBlur = useCallback(() => {
    if (!input || !onChangeFlag) return;
    let localValue = internalValue;

    const [hoursStr, minutesStr] = internalValue.split(':');
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);

    if (internalValue?.length === 1) localValue = `0${internalValue[0]}:00`;
    if (internalValue?.length === 3) {
      const idx = internalValue.indexOf(':');
      const zeros = '00';
      localValue = zeros.slice(0, 2 - idx) + internalValue + zeros.slice(0, idx);
    }
    if (internalValue?.length === 4) {
      if (internalValue.indexOf(':') === 2) localValue = `${internalValue}0`;
      else localValue = `0${internalValue}`;
    }
    if (!isValidHour(hours)) localValue = `23:${value.slice(3)}`;
    if (!isValidMinutes(minutes)) localValue = `${value.slice(0, 2)}:59`;

    if (interval && localValue) {
      localValue = validateInterval(localValue);
    }

    if (localValue) input.onChange(localValue);
    if (input?.onBlur) input.onBlur();
    if (onBlur) onBlur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalValue, input, onBlur]);

  const handlePatternClick = useCallback(() => {
    if (inputRef?.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    setInternalValue(input.value);
  }, [input.value]);

  return (
    <ThemeProvider theme={actualTheme}>
      <TimeInputContainer>
        <Input
          testId={testId}
          label={label}
          readOnly={readOnly || disabled}
          disabled={disabled}
          value={inputValue}
          input={input}
          meta={meta}
          autoComplete="off"
          size={size}
          variant={variant}
          helperText={helperText}
          showClearIcon={showClearIcon}
          placeholder={placeholder || ' '}
          onChange={handleChange}
          onClear={handleClear}
          rightAddOn={
            unit && (
              <UnitText
                size={size}
                hasError={hasError}
                disabled={disabled}
                data-testid={`${testId}-unit`}
              >
                {unit}
              </UnitText>
            )
          }
          onBlur={handleBlur}
          onFocus={onFocus}
          onMouseDown={onMouseDown}
        />
        {(inputValue?.length > 0 || !placeholder) && (
          <InputPattern
            variant={variant}
            hasError={hasError}
            size={size}
            onClick={handlePatternClick}
          >
            {pattern}
          </InputPattern>
        )}
      </TimeInputContainer>
    </ThemeProvider>
  );
};

export default TimeInput;
